import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import type { ClientMeDto } from '@xcentral/api-client';
import { useApi } from '../hooks/useApi';

interface ClientTiersContextValue {
  tiers: string[];
  accountId: string | null;
  loading: boolean;
  hasTier: (t: string) => boolean;
}

const DEFAULT_VALUE: ClientTiersContextValue = {
  tiers: [],
  accountId: null,
  loading: false,
  hasTier: () => false,
};

/**
 * Exported so a test can supply a resolved tier set directly, exactly as `AccountContext` is used by
 * `pages/accessibility.test.tsx` — the provider above does its OWN `/client-it/me` fetch and needs
 * MSAL, which would add an unrelated queued response to every positional `mockApi.get` audit.
 * #16315 (the Licences request-a-licence-type control is ClientAdmin-only).
 */
export const ClientTiersContext = createContext<ClientTiersContextValue>(DEFAULT_VALUE);

/** The no-provider default: no tiers, so every tier-gated control is ABSENT rather than dead. */
export const DEFAULT_CLIENT_TIERS: ClientTiersContextValue = DEFAULT_VALUE;

export type { ClientTiersContextValue };

/**
 * #12629 (CPT-2) — fetches the caller's client-portal tiers from GET /api/v1/client-it/me
 * once per authenticated session. The ID token does not carry the tiers; this is the
 * single source of truth the SPA reads from. A client with no tiers (403/404/any error)
 * is treated as normal (tiers: [], not an error state) — the caller may simply be a
 * ClientUser/ClientBilling with no admin-tier access.
 */
export function ClientTiersProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const api = useApi();
  const [tiers, setTiers] = useState<string[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setTiers([]);
      setAccountId(null);
      setLoading(false);
      return;
    }

    let mounted = true;
    const ctrl = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const me = await api.get<ClientMeDto>('/api/v1/client-it/me', ctrl.signal);
        if (!mounted) return;
        setAccountId(me.accountId ?? null);
        setTiers(me.tiers ?? []);
      } catch {
        // A client with no tiers (403/404/etc.) is normal, not an error — stop loading
        // with an empty tier set rather than surfacing a failure state.
        if (!mounted) return;
        setTiers([]);
        setAccountId(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [isAuthenticated, api]);

  const hasTier = (t: string) => tiers.includes(t);

  return (
    <ClientTiersContext.Provider value={{ tiers, accountId, loading, hasTier }}>
      {children}
    </ClientTiersContext.Provider>
  );
}

export function useClientTiers() {
  return useContext(ClientTiersContext);
}
